import { CommonModule, DatePipe } from '@angular/common';
import {
	Component,
	inject,
	Inject,
	ChangeDetectionStrategy,
	OnInit,
	signal
} from '@angular/core';
import {
	FormBuilder,
	FormControl,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
	Validators
} from '@angular/forms';
import {
	MAT_DIALOG_DATA,
	MatDialog,
	MatDialogRef
} from '@angular/material/dialog';
import { MaterialModule } from '@app/material.module';
import { HomeService } from '@pages/home/home.service';
import { DatepickerDialogComponent } from '@shared/datepicker-dialog/datepicker-dialog.component';
import { ProfileService } from '../../services/profile.service';
import { SelectCheckboxComponent } from '@app/shared/select-checkbox/select-checkbox.component';
import { DatepickerComponent } from '@app/shared/datepicker/datepicker.component';

@Component({
	selector: 'app-add-holidays',
	imports: [
		CommonModule,
		MaterialModule,
		FormsModule,
		ReactiveFormsModule,
		SelectCheckboxComponent,
		DatepickerComponent
	],
	templateUrl: './add-holidays.component.html',
	styleUrl: './add-holidays.component.css',
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [DatePipe]
})
export class AddHolidaysComponent implements OnInit {
	holidayForm!: FormGroup;
	isSubmitted = signal<boolean>(false);
	isEditPage = signal<boolean>(false);
	currentItem: any = null;

	// Signals for paytypes
	allPaytypes = signal<any[]>([]);
	isLoadingPaytypes = signal<boolean>(false);

	private readonly homeService: HomeService = inject(HomeService);
	private readonly profileService: ProfileService = inject(ProfileService);
	private readonly datePipe: DatePipe = inject(DatePipe);

	constructor(
		private readonly fb: FormBuilder,
		@Inject(MAT_DIALOG_DATA) public data: any,
		public dialog: MatDialog,
		public dialogRef: MatDialogRef<AddHolidaysComponent>
	) {
		this.isEditPage.set(this.data.isEdit);
		this.currentItem = this.data?.item;
		this.holidayForm = this.fb.group({
			HolidayName: new FormControl('', [Validators.required]),
			HolidayDate: new FormControl('', [Validators.required]),
			PayType: new FormControl([])
		});
	}

	ngOnInit(): void {
		this.loadPaytypes();
		if (this.currentItem) {
			this.patchFormValues();
		}
	}

	loadPaytypes(): void {
		this.isLoadingPaytypes.set(true);
		this.profileService.getAllPayTypes().subscribe({
			next: (response) => {
				const paytypes = response.map((item: any) => ({
					value: item.PayType,
					label: item.PayType
				}));
				this.allPaytypes.set(paytypes);
				this.isLoadingPaytypes.set(false);

				// After paytypes are loaded, patch form values if editing
				if (this.currentItem) {
					this.patchFormValues();
				}
			},
			error: (error) => {
				console.log('Error loading paytypes:', error);
				this.isLoadingPaytypes.set(false);
			}
		});
	}

	patchFormValues(): void {
		const formValues: any = {
			HolidayName: this.currentItem.HolidayName || '',
			HolidayDate:
				this.datePipe.transform(
					this.currentItem.HolidayDate,
					'MM/dd/yyyy'
				) || ''
		};

		// Handle PayType if it exists
		if (this.currentItem.PayType && this.allPaytypes().length > 0) {
			// Parse comma-separated paytypes
			const paytypeArray = this.currentItem.PayType.split(',').map(
				(pt: string) => pt.trim()
			);
			// Map to the format expected by select-checkbox component
			const selectedPaytypes = this.allPaytypes().filter((pt: any) =>
				paytypeArray.includes(pt.value)
			);
			formValues.PayType = selectedPaytypes;
		}

		this.holidayForm.patchValue(formValues);
	}

	openDateCalendar(): void {
		const dialogRef = this.dialog.open(DatepickerDialogComponent, {
			width: '450px', // Set the desired width
			panelClass: 'custom-sml-calendar',
			data: {
				showEvents: false
			}
		});

		dialogRef.afterClosed().subscribe((result) => {
			// Handle any actions after the dialog is closed
			this.holidayForm.patchValue({
				HolidayDate: result.date
			});
		});
	}

	onSave(): void {
		this.isSubmitted.set(true);
		if (this.holidayForm.valid) {
			const formValue = this.holidayForm.value;

			// Convert PayType array to comma-separated string
			let payTypeString = '';
			if (
				formValue.PayType &&
				Array.isArray(formValue.PayType) &&
				formValue.PayType.length > 0
			) {
				payTypeString = formValue.PayType.map((pt: any) =>
					pt.value ? pt.value : pt
				).join(',');
			}

			let payload: any = {
				HolidayName: formValue.HolidayName,
				HolidayDate: formValue.HolidayDate,
				PayType: payTypeString
			};

			if (this.isEditPage()) {
				payload.HolidayId = this.currentItem.HolidayId;
			}

			this.homeService.saveHolidaysObserved(payload).subscribe({
				next: (response) => {
					this.closeDialog();
				},
				error: (error) => {
					console.log('Error saving holiday:', error);
				}
			});
		}
	}

	closeDialog() {
		this.dialogRef.close('saved');
	}

	closeHolidayDialog(): void {
		this.dialogRef.close();
	}
}
